import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {PreSalesFiltersResponse, PreSalesReportData, SingleResult} from 'utils/types';
import {addResultCases} from 'utils/helpers';
import ApiService from 'redux/services/api-service';

export interface IState {
  preSalesReportData: SingleResult<PreSalesReportData>;
  calculatedSalesTimeSeries?: {
    [timeFrameType: string]: {loading: boolean; data: any[]; error?: object};
  };
  calculatedItemSales?: {[key: string]: {loading: boolean; data: any[]; error?: object}}; // key = grouping signature
  calculatedGeoSales?: {[key: string]: {loading: boolean; data: any[]; error?: object}}; // key = grouping signature
  salesReportFilters: {
    options: {
      brands: string[];
      states: Array<{label: string; value: string}>;
      dmas: Array<{label: string; value: string}>;
      areas: Array<{label: string; value: string}>;
      earliest_date?: string;
      latest_date?: string;
      loading: boolean;
    };
    selected: {
      brand: string[];
      state: string[];
      dma: string[];
      area: string[];
      start_date?: string;
      end_date?: string;
    };
    lastFetchedSignature: {
      time?: string;
      item?: string;
      geo?: string;
    };
    currentCampaignId?: number;
    hasInitialDataFetch?: boolean;
  };
}

type PreSalesFiltersRequest = {
  campaign_id: number;
  category?: string;
};

export const dashboardEndpoints = {
  getPreSalesFiltersData: createAsyncThunk<PreSalesFiltersResponse, PreSalesFiltersRequest>(
    'dashboard/getPreSalesFiltersData',
    async payload => {
      const params = new URLSearchParams();
      params.append('campaign_id', String(payload.campaign_id));
      if (payload.category) {
        params.append('category', payload.category);
      }
      const response = await ApiService.get(`/pre_sales_filters/?${params.toString()}`);
      return response.data;
    },
  ),
  getCalculatedSalesFilters: createAsyncThunk('dashboard/getCalculatedSalesFilters', async (payload: any) => {
    const response = await ApiService.get(`/calculated_sales/filters/?${new URLSearchParams(payload)}`);
    return {
      ...response.data,
      campaign_id: payload.campaign_id,
    };
  }),
  getCalculatedSalesTimeSeries: createAsyncThunk('dashboard/getCalculatedSalesTimeSeries', async (payload: any) => {
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v as string));
      } else if (value !== undefined && value !== null) {
        params.append(key, value as string);
      }
    });
    const response = await ApiService.get(`/calculated_sales/time_series/?${params.toString()}`);
    return {
      data: response.data,
      timeFrameType: payload.time_frame_type || 'month',
    };
  }),
  getCalculatedItemSales: createAsyncThunk('dashboard/getCalculatedItemSales', async (payload: any) => {
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v as string));
      } else if (value !== undefined && value !== null) {
        params.append(key, value as string);
      }
    });
    const response = await ApiService.get(`/calculated_sales/items/?${params.toString()}`);
    return {data: response.data, args: payload};
  }),
  getCalculatedGeoSales: createAsyncThunk('dashboard/getCalculatedGeoSales', async (payload: any) => {
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v as string));
      } else if (value !== undefined && value !== null) {
        params.append(key, value as string);
      }
    });
    const response = await ApiService.get(`/calculated_sales/geo/?${params.toString()}`);
    return {data: response.data, args: payload};
  }),
  getPreSalesReportData: createAsyncThunk(
    'dashboard/getPreSalesReportData',
    async (payload: any, {rejectWithValue}) => {
      const params = new URLSearchParams();
      params.append('campaign_id', payload.campaign_id);
      params.append('category', payload.category);
      params.append('brand', payload.brand);
      try {
        const response = await ApiService.get(`/pre_sales_report/?${params.toString()}`);
        return {as: 'success', value: response.data};
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
};
export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    preSalesReportData: {as: 'idle'},
    calculatedSalesTimeSeries: {},
    calculatedItemSales: {},
    calculatedGeoSales: {},
    salesReportFilters: {
      options: {
        brands: [],
        states: [],
        dmas: [],
        areas: [],
        earliest_date: undefined,
        latest_date: undefined,
        loading: false,
      },
      selected: {
        brand: [],
        state: [],
        dma: [],
        area: [],
        start_date: undefined,
        end_date: undefined,
      },
      lastFetchedSignature: {
        time: undefined,
        item: undefined,
        geo: undefined,
      },
      currentCampaignId: undefined,
      hasInitialDataFetch: false,
    },
  } as IState,
  reducers: {
    setSelectedFilters: (
      state,
      action: PayloadAction<
        Partial<{
          brand: string[];
          state: string[];
          dma: string[];
          start_date?: string;
          end_date?: string;
        }>
      >,
    ) => {
      state.salesReportFilters.selected = {
        ...state.salesReportFilters.selected,
        ...action.payload,
      };
    },
    setLastFetchedSignature: (state, action: PayloadAction<{tab: 'time' | 'item' | 'geo'; signature: string}>) => {
      state.salesReportFilters.lastFetchedSignature[action.payload.tab] = action.payload.signature;
    },
    setHasInitialDataFetch: (state, action: PayloadAction<boolean>) => {
      state.salesReportFilters.hasInitialDataFetch = action.payload;
    },
    clearCampaignData: state => {
      // Clear all calculated sales data
      state.calculatedSalesTimeSeries = {};
      state.calculatedItemSales = {};
      state.calculatedGeoSales = {};

      // Clear selected filters
      state.salesReportFilters.selected = {
        brand: [],
        state: [],
        dma: [],
        area: [],
        start_date: undefined,
        end_date: undefined,
      };

      // Clear last fetched signatures
      state.salesReportFilters.lastFetchedSignature = {
        time: undefined,
        item: undefined,
        geo: undefined,
      };

      // Reset initial data fetch flag
      state.salesReportFilters.hasInitialDataFetch = false;
    },
  },
  extraReducers: builder => {
    addResultCases(builder, dashboardEndpoints.getPreSalesReportData, 'preSalesReportData');
    builder
      .addCase(dashboardEndpoints.getCalculatedSalesFilters.pending, state => {
        state.salesReportFilters.options.loading = true;
      })
      .addCase(dashboardEndpoints.getCalculatedSalesFilters.fulfilled, (state, {payload}) => {
        state.salesReportFilters.options.loading = false;
        state.salesReportFilters.options.brands = payload.brands || [];
        state.salesReportFilters.options.states = (payload.states || []).map((s: any) =>
          typeof s === 'string' ? {label: s, value: s} : s,
        );
        state.salesReportFilters.options.dmas = (payload.dmas || []).map((d: any) =>
          typeof d === 'string' ? {label: d, value: d} : d,
        );
        state.salesReportFilters.options.areas = (payload.areas || []).map((a: any) =>
          typeof a === 'string' ? {label: a, value: a} : a,
        );
        state.salesReportFilters.options.earliest_date = payload.earliest_date;
        state.salesReportFilters.options.latest_date = payload.latest_date;
        state.salesReportFilters.currentCampaignId = payload.campaign_id;
        // Set default dates in selected if they exist and selected dates are empty
        if (payload.earliest_date && !state.salesReportFilters.selected.start_date) {
          state.salesReportFilters.selected.start_date = payload.earliest_date;
        }
        if (payload.latest_date && !state.salesReportFilters.selected.end_date) {
          state.salesReportFilters.selected.end_date = payload.latest_date;
        }
      })
      .addCase(dashboardEndpoints.getCalculatedSalesFilters.rejected, state => {
        state.salesReportFilters.options.loading = false;
      })
      .addCase(dashboardEndpoints.getCalculatedSalesTimeSeries.pending, (state, {meta}) => {
        if (!state.calculatedSalesTimeSeries) state.calculatedSalesTimeSeries = {};
        const timeFrameType = meta.arg.time_frame_type || 'month';
        state.calculatedSalesTimeSeries[timeFrameType] = {loading: true, data: [], error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedSalesTimeSeries.fulfilled, (state, {payload}) => {
        if (!state.calculatedSalesTimeSeries) state.calculatedSalesTimeSeries = {};
        const {data, timeFrameType} = payload;
        state.calculatedSalesTimeSeries[timeFrameType] = {loading: false, data, error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedSalesTimeSeries.rejected, (state, {payload, meta}) => {
        if (!state.calculatedSalesTimeSeries) state.calculatedSalesTimeSeries = {};
        const timeFrameType = meta.arg.time_frame_type || 'month';
        state.calculatedSalesTimeSeries[timeFrameType] = {
          loading: false,
          data: [],
          error: (payload as object) || {message: 'Error fetching time series'},
        };
      });
    builder
      .addCase(dashboardEndpoints.getCalculatedItemSales.pending, (state, {meta}) => {
        if (!state.calculatedItemSales) state.calculatedItemSales = {};
        const sig = JSON.stringify({
          group_by: meta.arg.group_by || 'item',
          time_frame_type: meta.arg.time_frame_type,
          brand: meta.arg.brand,
          state: meta.arg.state,
          dma: meta.arg.dma,
          start_date: meta.arg.start_date,
          end_date: meta.arg.end_date,
        });
        state.calculatedItemSales[sig] = {loading: true, data: [], error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedItemSales.fulfilled, (state, {payload, meta}) => {
        if (!state.calculatedItemSales) state.calculatedItemSales = {};
        const args = payload.args || meta.arg;
        const sig = JSON.stringify({
          group_by: args.group_by || 'item',
          time_frame_type: args.time_frame_type,
          brand: args.brand,
          state: args.state,
          dma: args.dma,
          start_date: args.start_date,
          end_date: args.end_date,
        });
        state.calculatedItemSales[sig] = {loading: false, data: payload.data, error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedItemSales.rejected, (state, {payload, meta}) => {
        if (!state.calculatedItemSales) state.calculatedItemSales = {};
        const sig = JSON.stringify({
          group_by: meta.arg.group_by || 'item',
          time_frame_type: meta.arg.time_frame_type,
          brand: meta.arg.brand,
          state: meta.arg.state,
          dma: meta.arg.dma,
          start_date: meta.arg.start_date,
          end_date: meta.arg.end_date,
        });
        state.calculatedItemSales[sig] = {
          loading: false,
          data: [],
          error: (payload as object) || {message: 'Error fetching item sales'},
        };
      });
    builder
      .addCase(dashboardEndpoints.getCalculatedGeoSales.pending, (state, {meta}) => {
        if (!state.calculatedGeoSales) state.calculatedGeoSales = {};
        const sig = JSON.stringify({
          group_by: meta.arg.group_by || 'state,time',
          time_frame_type: meta.arg.time_frame_type,
          brand: meta.arg.brand,
          start_date: meta.arg.start_date,
          end_date: meta.arg.end_date,
        });
        state.calculatedGeoSales[sig] = {loading: true, data: [], error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedGeoSales.fulfilled, (state, {payload, meta}) => {
        if (!state.calculatedGeoSales) state.calculatedGeoSales = {};
        const args = payload.args || meta.arg;
        const sig = JSON.stringify({
          group_by: args.group_by || 'state,time',
          time_frame_type: args.time_frame_type,
          brand: args.brand,
          start_date: args.start_date,
          end_date: args.end_date,
        });
        state.calculatedGeoSales[sig] = {loading: false, data: payload.data, error: undefined};
      })
      .addCase(dashboardEndpoints.getCalculatedGeoSales.rejected, (state, {payload, meta}) => {
        if (!state.calculatedGeoSales) state.calculatedGeoSales = {};
        const sig = JSON.stringify({
          group_by: meta.arg.group_by || 'state,time',
          time_frame_type: meta.arg.time_frame_type,
          brand: meta.arg.brand,
          start_date: meta.arg.start_date,
          end_date: meta.arg.end_date,
        });
        state.calculatedGeoSales[sig] = {
          loading: false,
          data: [],
          error: (payload as object) || {message: 'Error fetching geo sales'},
        };
      });
  },
});

export const dashboardActions = dashboardSlice.actions;

export type DashboardfSlice = {
  [dashboardSlice.name]: ReturnType<(typeof dashboardSlice)['reducer']>;
};
